import { ChartType } from '@adtrackify/at-tracking-event-types';

import { WidgetSkeleton } from './widget-skeleton';

import { LoadingRegion } from '@/components/ui/loading-region';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface DashboardGridSkeletonProps {
  className?: string;
  widgetCount?: number;
  showRealisticLayout?: boolean;
}

const DEFAULT_WIDGETS = [
  { id: 'kpi-1', type: ChartType.KPI, gridClass: 'col-span-3 row-span-1' },
  { id: 'kpi-2', type: ChartType.KPI, gridClass: 'col-span-3 row-span-1' },
  { id: 'kpi-3', type: ChartType.KPI, gridClass: 'col-span-3 row-span-1' },
  { id: 'kpi-4', type: ChartType.KPI, gridClass: 'col-span-3 row-span-1' },
  { id: 'area-1', type: ChartType.AREA, gridClass: 'col-span-6 row-span-3' },
  { id: 'bar-1', type: ChartType.BAR, gridClass: 'col-span-6 row-span-3' },
  { id: 'table-1', type: ChartType.TABLE, gridClass: 'col-span-12 row-span-4' },
];

export function DashboardGridSkeleton({
  className,
  widgetCount = 6,
  showRealisticLayout = true,
}: DashboardGridSkeletonProps) {
  // Get widgets to display (up to widgetCount)
  const widgets = DEFAULT_WIDGETS.slice(0, widgetCount);

  if (!showRealisticLayout) {
    // Simple grid layout
    return (
      <LoadingRegion
        aria-label="Dashboard"
        className={cn('grid grid-cols-1 gap-4 p-4 md:grid-cols-2 lg:grid-cols-3', className)}
        label="Loading dashboard…"
      >
        {Array.from({ length: widgetCount }).map((_, index) => (
          <div key={index} className="h-64 rounded-lg">
            <WidgetSkeleton ownLoadingRegion={false} />
          </div>
        ))}
      </LoadingRegion>
    );
  }

  // Realistic dashboard grid layout (mimics the actual grid)
  return (
    <LoadingRegion
      aria-label="Dashboard"
      className={cn('h-full w-full', className)}
      label="Loading dashboard…"
    >
      {/* Dashboard toolbar skeleton */}
      <div className="mb-4 flex items-center justify-between p-4">
        <div className="flex items-center gap-4">
          <Skeleton aria-hidden="true" className="h-10 w-48" />
          <Skeleton aria-hidden="true" className="h-10 w-32" />
        </div>
        <div className="flex gap-2">
          <Skeleton aria-hidden="true" className="h-10 w-24" />
          <Skeleton aria-hidden="true" className="h-10 w-10" />
        </div>
      </div>

      {/* Filter toolbar skeleton */}
      <div className="mb-4 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Skeleton aria-hidden="true" className="h-9 w-32" />
            <Skeleton aria-hidden="true" className="h-9 w-40" />
            <Skeleton aria-hidden="true" className="h-9 w-28" />
          </div>
          <Skeleton aria-hidden="true" className="h-9 w-9 rounded" />
        </div>
      </div>

      {/* Grid layout skeleton */}
      <div className="p-4">
        <div className="grid grid-cols-12 gap-3" style={{ gridAutoRows: '136px' }}>
          {widgets.map(widget => (
            <div key={widget.id} className={cn('rounded-lg', widget.gridClass)}>
              <WidgetSkeleton chartType={widget.type} ownLoadingRegion={false} />
            </div>
          ))}
        </div>
      </div>
    </LoadingRegion>
  );
}

export function MinimalGridSkeleton({ className }: { className?: string }) {
  return (
    <LoadingRegion
      aria-label="Dashboard"
      className={cn('h-full w-full p-4', className)}
      label="Loading dashboard…"
    >
      <div className="grid grid-cols-12 gap-3" style={{ gridAutoRows: '136px' }}>
        {/* Quick KPI widgets */}
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="col-span-3 row-span-1">
            <Skeleton aria-hidden="true" className="h-full w-full rounded-lg" />
          </div>
        ))}

        {/* Chart widgets */}
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i + 4} className="col-span-6 row-span-3">
            <Skeleton aria-hidden="true" className="h-full w-full rounded-lg" />
          </div>
        ))}

        {/* Table widget */}
        <div className="col-span-12 row-span-4">
          <Skeleton aria-hidden="true" className="h-full w-full rounded-lg" />
        </div>
      </div>
    </LoadingRegion>
  );
}
